import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

interface RefreshContextType {
  // State
  canAutoRefresh: boolean;
  isPaused: boolean;
  pauseReason: string | null;
  lastActivity: Date;

  // Actions
  setModalOpen: (open: boolean) => void;
  setHasUnsavedChanges: (has: boolean) => void;
  setIsTyping: (typing: boolean) => void;
  markActivity: () => void;

  // For debugging
  debugState: {
    modalOpen: boolean;
    hasUnsavedChanges: boolean;
    isTyping: boolean;
    secondsSinceActivity: number;
  };
}

const RefreshContext = createContext<RefreshContextType | undefined>(undefined);

// How long after last activity before auto-refresh is allowed
const ACTIVITY_COOLDOWN_MS = 10000; // 10 seconds

export const RefreshProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [modalOpen, setModalOpenState] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChangesState] = useState(false);
  const [isTyping, setIsTypingState] = useState(false);
  const [lastActivity, setLastActivity] = useState(new Date());
  const [secondsSinceActivity, setSecondsSinceActivity] = useState(0);

  // Track if we're past the activity cooldown
  const [activityCooldownPassed, setActivityCooldownPassed] = useState(true);

  // Refs to track focus state
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Mark user activity
  const markActivity = useCallback(() => {
    setLastActivity(new Date());
    setActivityCooldownPassed(false);
  }, []);

  // Set modal open state
  const setModalOpen = useCallback((open: boolean) => {
    setModalOpenState(open);
    if (open) markActivity();
  }, [markActivity]);

  // Set unsaved changes state
  const setHasUnsavedChanges = useCallback((has: boolean) => {
    setHasUnsavedChangesState(has);
    if (has) markActivity();
  }, [markActivity]);

  // Set typing state with debounce
  const setIsTyping = useCallback((typing: boolean) => {
    if (typing) {
      setIsTypingState(true);
      markActivity();

      // Clear any existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      // Auto-clear typing state after 2 seconds of no typing
      typingTimeoutRef.current = setTimeout(() => {
        setIsTypingState(false);
      }, 2000);
    } else {
      setIsTypingState(false);
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    }
  }, [markActivity]);

  // Track activity cooldown
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const elapsed = now - lastActivity.getTime();
      setSecondsSinceActivity(Math.floor(elapsed / 1000));

      if (elapsed >= ACTIVITY_COOLDOWN_MS) {
        setActivityCooldownPassed(true);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [lastActivity]);

  // Global event listeners for activity detection
  useEffect(() => {
    const handleActivity = () => {
      markActivity();
    };

    const handleFocus = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable
      ) {
        setIsTyping(true);
      }
    };

    const handleBlur = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT' ||
        target.isContentEditable
      ) {
        // Delay to allow for clicking submit buttons
        setTimeout(() => setIsTyping(false), 500);
      }
    };

    const handleKeydown = () => {
      setIsTyping(true);
      markActivity();
    };

    // Mouse and scroll activity
    window.addEventListener('mousedown', handleActivity);
    window.addEventListener('scroll', handleActivity, { passive: true });

    // Input focus tracking
    document.addEventListener('focusin', handleFocus);
    document.addEventListener('focusout', handleBlur);
    document.addEventListener('keydown', handleKeydown);

    return () => {
      window.removeEventListener('mousedown', handleActivity);
      window.removeEventListener('scroll', handleActivity);
      document.removeEventListener('focusin', handleFocus);
      document.removeEventListener('focusout', handleBlur);
      document.removeEventListener('keydown', handleKeydown);
    };
  }, [markActivity, setIsTyping]);

  // Cleanup typing timeout on unmount
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  // Calculate if auto-refresh is allowed
  const canAutoRefresh = !modalOpen && !hasUnsavedChanges && !isTyping && activityCooldownPassed;

  // Determine pause reason for UI feedback
  let pauseReason: string | null = null;
  if (modalOpen) {
    pauseReason = 'Modal open';
  } else if (hasUnsavedChanges) {
    pauseReason = 'Unsaved changes';
  } else if (isTyping) {
    pauseReason = 'Typing';
  } else if (!activityCooldownPassed) {
    pauseReason = 'Recent activity';
  }

  const value: RefreshContextType = {
    canAutoRefresh,
    isPaused: !canAutoRefresh,
    pauseReason,
    lastActivity,
    setModalOpen,
    setHasUnsavedChanges,
    setIsTyping,
    markActivity,
    debugState: {
      modalOpen,
      hasUnsavedChanges,
      isTyping,
      secondsSinceActivity,
    },
  };

  return (
    <RefreshContext.Provider value={value}>
      {children}
    </RefreshContext.Provider>
  );
};

export const useRefreshContext = (): RefreshContextType => {
  const context = useContext(RefreshContext);
  if (!context) {
    // Return a default context for components outside the provider
    return {
      canAutoRefresh: true,
      isPaused: false,
      pauseReason: null,
      lastActivity: new Date(),
      setModalOpen: () => {},
      setHasUnsavedChanges: () => {},
      setIsTyping: () => {},
      markActivity: () => {},
      debugState: {
        modalOpen: false,
        hasUnsavedChanges: false,
        isTyping: false,
        secondsSinceActivity: 0,
      },
    };
  }
  return context;
};

export default RefreshContext;
